import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { DashboardPanelSlotId } from '@/products/helpdesk/features/dashboard/dashboard-layout';
import { cn } from '@/lib/utils';

export type { DashboardPanelSlotId };

export function DashboardPanel({
  slotId,
  title,
  description,
  action,
  children,
  className,
}: {
  slotId: DashboardPanelSlotId;
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card
      data-dashboard-slot={slotId}
      className={cn('min-w-0 gap-0 overflow-hidden py-0 shadow-sm', className)}
    >
      <CardHeader className="border-b py-4">
        <CardTitle className="min-w-0 text-base">
          <h2 className="truncate text-base font-semibold">{title}</h2>
        </CardTitle>
        {description ? (
          <p className="min-w-0 text-sm break-words text-muted-foreground">{description}</p>
        ) : null}
        {action ? <CardAction>{action}</CardAction> : null}
      </CardHeader>
      <CardContent className="min-w-0 space-y-3 px-6 py-4">{children}</CardContent>
    </Card>
  );
}

export function DashboardPanelLinkAction({ to, label }: { to: string; label: string }) {
  return (
    <Button asChild variant="outline" size="sm">
      <Link to={to}>{label}</Link>
    </Button>
  );
}

/** Empty chart area without inventing metrics (placeholders / coming-soon). */
export function DashboardPlaceholderChart({ label }: { label: string }) {
  return (
    <div
      className="flex h-28 items-center justify-center rounded-md border border-dashed border-muted-foreground/25 bg-muted/30 px-3 text-center text-sm text-muted-foreground"
      aria-hidden
    >
      {label}
    </div>
  );
}

export function DashboardPhaseHint({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}
